/* eslint-disable @typescript-eslint/no-misused-promises */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import {
  companyOpportunityInterviewSchema,
  CompanyOpportunityInterviewSchema,
} from "./schema";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/ui/multi-select";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import PreprocessIcon from "@/assets/preprocess_icon.svg";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { useCompanyTeamQuery } from "@/queries/company/team";

import { CompanyInterview } from "@/types";
import useCompanyInterviewMutation from "./mutations";

interface BaseProps {
  interview?: CompanyInterview;
  candidateId?: string;
  jobOpeningId: string;
  companyId: string;
  trigger?: React.ReactNode;
  onSuccess?: (data: CompanyInterview) => void;
}

type Props = BaseProps &
  (
    | {
        interview: CompanyInterview;
        candidateId?: never;
      }
    | {
        interview?: never;
        candidateId: string;
      }
  );

export interface CreateUpdateInterviewDialogHandle {
  open: () => void;
}

const CreateUpdateInterviewDialog = forwardRef<
  CreateUpdateInterviewDialogHandle,
  Props
>((props: Props, ref) => {
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState(false);

  const { companyTeam } = useCompanyTeamQuery(props.companyId);

  // Ugly hack so that we can trigger the dialog from a dropdown menu
  // We can't use the dropdown as the dialog trigger because it would close when clicked
  useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
  }));

  const onSuccess = (result: CompanyInterview) => {
    props.onSuccess?.(result);

    // If we were updating, close
    if (props.interview) {
      setOpen(false);
      // If we were creating, swap content to explain next steps to user
    } else {
      setSuccess(true);
    }
  };

  const {
    mutate,
    isPending: mutationLoading,
    isError: mutationError,
  } = useCompanyInterviewMutation(
    props.jobOpeningId,
    props.interview?.id ?? "",
    props.candidateId ?? "",
    onSuccess,
  );

  const form = useForm<CompanyOpportunityInterviewSchema>({
    resolver: async (data, context, opts) => {
      return zodResolver(companyOpportunityInterviewSchema)(
        data,
        context,
        opts,
      );
    },
    defaultValues: {
      id: props.interview?.id,
      name: props.interview?.name ?? "",
      duration: props.interview?.duration ?? 30,
      participants: props.interview?.participants ?? [],
    },
  });

  useEffect(() => {
    form.reset({
      id: props.interview?.id,
      name: props.interview?.name ?? "",
      duration: props.interview?.duration ?? 30,
      participants: props.interview?.participants ?? [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, props.interview]);

  const participantOptions = useMemo(() => {
    if (!companyTeam) return [];

    return companyTeam
      .map((member) => ({
        label: member.name ?? member.email ?? "",
        value: member.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [companyTeam]);

  function onSubmit(data: CompanyOpportunityInterviewSchema) {
    if (mutationLoading) return;
    mutate(data);
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!ref && (
        <DialogTrigger asChild>
          {props.trigger ?? (
            <Button variant="secondary">
              {props.interview && !success ? "Update" : "Add"} Interview
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="w-full max-w-[min(calc(100vw-2rem),768px)] p-0">
        <DialogHeader className="p-6">
          <div className="flex items-center gap-2">
            <img src={PreprocessIcon} alt="Preprocess Icon" className="" />
            <DialogTitle>
              {props.interview && !success ? "Update" : "Add"} an Interview
            </DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Enter the details of the interview here and click &quot;Save&quot;
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(100vh-10rem)] md:max-h-[70vh] px-6 pb-6">
          {success ? (
            <>
              <p className="text-sm text-muted-foreground">
                Thank you for requesting an interview! The candidate has been
                notified and asked to provide their availability. Once they do,
                you can schedule the interview.
              </p>
              <div
                className={`flex ${props.interview ? "justify-between" : "justify-end"}`}
              >
                <Button
                  type="button"
                  variant="outline"
                  className="mt-2"
                  onClick={() => {
                    setOpen(false);
                    setSuccess(false);
                  }}
                >
                  Close
                </Button>
              </div>
            </>
          ) : (
            <Form {...form}>
              <div className="space-y-4 w-full">
                {mutationError && (
                  <p className="text-center w-full border border-destructive rounded-md text-destructive p-2 bg-destructive/10">
                    Something went wrong when updating the interview, please try
                    again later.
                  </p>
                )}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Interview Name</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="First Interview" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="duration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Duration (minutes)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          min={1}
                          step={1}
                          placeholder="30"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="participants"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Interviewer(s)</FormLabel>
                      <FormControl>
                        <MultiSelect
                          values={field.value}
                          setValues={field.onChange}
                          labeledOptions={participantOptions}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div
                  className={`flex ${props.interview ? "justify-between" : "justify-end"}`}
                >
                  <Button
                    type="button"
                    className="mt-2"
                    onClick={form.handleSubmit(onSubmit)}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
});
CreateUpdateInterviewDialog.displayName = "CreateUpdateInterviewDialog";

export default CreateUpdateInterviewDialog;
